import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import logo from '../assets/bharat.png';

const stats = [
    { end: 50, suffix: "+", label: "Years of Experience" },
    { end: 150, suffix: "+", label: "Live Titles" },
    { end: 250, suffix: "K+", label: "Readers Nationwide" },
    { end: 4, suffix: ".8★", label: "Avg Rating" },
];

const domains = [
    { icon: "🎓", label: "B.Com / M.Com / LL.B. / MBA" },
    { icon: "📘", label: "CA Inter / CPT" },
    { icon: "📗", label: "CS Executive" },
    { icon: "📙", label: "CMA Inter" },
    { icon: "🏆", label: "CA / CS / CMA Final" },
    { icon: "🏛️", label: "Civil & Criminal Laws" },
    { icon: "🏢", label: "Corporate & Commercial Laws" },
    { icon: "💸", label: "GST, Service Tax & Indirect Tax" },
    { icon: "📊", label: "Income Tax" },
    { icon: "👷", label: "Labour Laws" },
];

const pillars = [
    { icon: "📚", title: "Vast Publications", desc: "Maintaining over 150 live titles and annual publications. India's intelligentsia — judges, CAs, lawyers, CS, and professors — are associated in our publications." },
    { icon: "🖨️", title: "Printing & Binding", desc: "Professionally qualified team with strong in-house editorial and DTP capabilities. State-of-the-art machines for qualitative binding and printing jobs." },
    { icon: "✍️", title: "Invitation to Authors", desc: "We invite proposals from authors interested in writing books for the legal profession, corporate sector, CA/CS/ICWA, Management, and Finance." },
];

function FloatingBadge({ icon, label }) {
    return (
        <div style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: "white", border: "1.5px solid #f0d0c8",
            borderRadius: 50, padding: "8px 16px",
            fontSize: 13, fontWeight: 700, color: "#8B1A1A",
            boxShadow: "0 4px 14px rgba(192,57,43,0.12)",
            whiteSpace: "nowrap",
        }}>
            <span>{icon}</span>{label}
        </div>
    );
}

export default function AboutUs() {
    const [visible, setVisible] = useState(false);
    const statsRef = useRef();

    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.3 });
        if (statsRef.current) obs.observe(statsRef.current);
        return () => obs.disconnect();
    }, []);

    return (
        <div className="about-page" style={{ background: "#FDF5F5" }}>
            <style>{`
                @media (max-width: 1024px) {
                    .about-section { padding: 40px 24px !important; }
                    .about-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
                    .about-stats-grid { grid-template-columns: 1fr 1fr !important; gap: 24px !important; }
                    .about-pillars-grid { grid-template-columns: 1fr !important; }
                    .about-domains-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important; }
                    .about-hero-visual { height: 360px !important; }
                    .story-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
                }
                @media (max-width: 768px) {
                    .about-stats-grid { grid-template-columns: 1fr !important; }
                }
            `}</style>
            {/* ── Hero / About intro ─────────────────────────────────── */}
            <section className="about-section" style={{ padding: "80px 72px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(192,57,43,0.07) 1.5px, transparent 1.5px)", backgroundSize: "28px 28px", pointerEvents: "none" }} />
                <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 5% 95%, rgba(192,57,43,0.07) 0%, transparent 50%), radial-gradient(circle at 95% 5%, rgba(212,168,71,0.06) 0%, transparent 50%)", pointerEvents: "none" }} />

                <div className="about-hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center", maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>

                    {/* Left: visual */}
                    <div className="about-hero-visual" style={{ position: "relative", height: 480 }}>
                        <div style={{
                            position: "absolute", top: "50%", left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 220, height: 220, borderRadius: "50%",
                            background: "linear-gradient(135deg,rgb(255, 253, 253),rgb(255, 255, 255))",
                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                            boxShadow: "0 16px 48px rgba(192,57,43,0.35)",
                            zIndex: 4, animation: "float 4s ease-in-out infinite"
                        }}>
                            <img src={logo} alt="logo" />
                        </div>
                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 320, height: 320, borderRadius: "50%", border: "1.5px dashed rgba(192,57,43,0.2)", pointerEvents: "none" }} />
                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 440, height: 440, borderRadius: "50%", border: "1.5px dashed rgba(192,57,43,0.1)", pointerEvents: "none" }} />
                        {domains.slice(0, 6).map((d, i) => {
                            const angle = (i / 6) * 360;
                            const rad = (angle * Math.PI) / 180;
                            const r = 195;
                            return (
                                <div key={i} style={{
                                    position: "absolute",
                                    top: `calc(50% + ${Math.sin(rad) * r}px)`,
                                    left: `calc(50% + ${Math.cos(rad) * r}px)`,
                                    transform: "translate(-50%, -50%)",
                                    animation: `float${(i % 3) + 1} ${3 + i * 0.4}s ease-in-out infinite`,
                                    zIndex: 3
                                }}>
                                    <FloatingBadge icon={d.icon} label={d.label} />
                                </div>
                            );
                        })}
                        <div style={{
                            position: "absolute", bottom: 20, right: 10,
                            background: "white", borderRadius: 18, border: "1.5px solid #f0d0c8",
                            padding: "14px 20px", textAlign: "center",
                            boxShadow: "0 6px 20px rgba(192,57,43,0.12)",
                            animation: "float2 3.5s ease-in-out infinite", zIndex: 5
                        }}>
                            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 900, color: "#C0392B", lineHeight: 1 }}>50+</div>
                            <div style={{ fontSize: 11, color: "#b07878", fontWeight: 600, marginTop: 2 }}>Years of Trust</div>
                        </div>
                    </div>

                    {/* Right: copy */}
                    <div>

                        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 900, color: "#1a0a08", lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: 20 }}>
                            Bharat Law House<br /><span style={{ color: "#C0392B", fontStyle: "italic" }}>Pvt. Ltd.</span>
                        </h2>
                        <p style={{ fontSize: 15, color: "#7a5550", lineHeight: 1.8, marginBottom: 16 }}>
                            <strong style={{ color: "#1a0a08" }}>Bharat</strong> is a trusted trade-name for a group of concerns — popularly known as Bharat Law House and B.L.H. Publishers' Distributors Pvt. Ltd. In the law books business for nearly five decades, managed by a single family of father and three accomplished sons.
                        </p>
                        <p style={{ fontSize: 15, color: "#7a5550", lineHeight: 1.8, marginBottom: 32 }}>
                            One of India's leading law book publishers, Bharat covers taxation, company law, capital markets, finance, industrial law, foreign exchange, commercial, civil and criminal laws — a familiar name with CAs, company secretaries, corporate executives, and the judiciary.
                        </p>
                        <Link to="/contact-us">
                            <button style={{
                                padding: "14px 32px", fontSize: 15, fontWeight: 700,
                                background: "linear-gradient(135deg, #C0392B, #96221A)",
                                color: "white", border: "none", borderRadius: 10,
                                cursor: "pointer", fontFamily: "'Nunito Sans',sans-serif",
                                boxShadow: "0 4px 18px rgba(192,57,43,0.3)", transition: "all 0.2s"
                            }}>Contact Us →</button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Stats bar ─────────────────────────────────────────── */}
            <section className="about-section" style={{ background: "linear-gradient(135deg, #6B1A1A 0%, #521010 60%, #611515 100%)", padding: "48px 72px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.1) 1.5px, transparent 1.5px)", backgroundSize: "28px 28px", pointerEvents: "none" }} />
                <div className="about-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 32, maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
                    {stats.map(({ end, suffix, label }) => (
                        <div key={label} style={{ textAlign: "center" }}>
                            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 44, fontWeight: 900, color: "white", lineHeight: 1 }}>
                                {visible ? <CountUp end={end} duration={2} /> : "0"}{suffix}
                            </div>
                            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, fontWeight: 600, marginTop: 6, letterSpacing: "0.04em" }}>{label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Our Story (replaces timeline) ──────────────────────── */}
            <section className="about-section" style={{ padding: "80px 72px", background: "white" }}>
                <div className="story-grid" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 72, alignItems: "center" }}>
                    {/* Left: large pull quote */}
                    <div style={{ position: "relative" }}>
                        <div style={{ position: "absolute", top: -12, left: -16, fontFamily: "'Playfair Display',serif", fontSize: 120, lineHeight: 1, color: "rgba(192,57,43,0.08)", fontWeight: 900, pointerEvents: "none", userSelect: "none" }}>"</div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 50, padding: "5px 14px", marginBottom: 20 }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#C0392B" }} />
                            <span style={{ color: "#C0392B", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em" }}>OUR STORY</span>
                        </div>
                        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, fontWeight: 900, color: "#1a0a08", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
                            Five decades of<br /><span style={{ color: "#C0392B", fontStyle: "italic" }}>trusted knowledge.</span>
                        </h2>
                        <div style={{ width: 48, height: 3, background: "linear-gradient(90deg, #C0392B, #E8523A)", borderRadius: 2, marginTop: 24 }} />
                    </div>

                    {/* Right: prose */}
                    <div>
                        <p style={{ fontSize: 15, color: "#7a5550", lineHeight: 1.9, marginBottom: 18 }}>
                            Founded nearly five decades ago in New Delhi, Bharat Law House began with a simple conviction: that complex legal and financial knowledge should be accessible to every professional who needs it. What started as a family-run publishing house has grown into one of India's most trusted names in law and finance publishing.
                        </p>
                        <p style={{ fontSize: 15, color: "#7a5550", lineHeight: 1.9, marginBottom: 18 }}>
                            Over the years, Bharat has built a diverse catalogue spanning direct and indirect taxation, company law, capital markets, industrial law, and criminal law — serving chartered accountants, company secretaries, corporate executives, consultants, and the judiciary alike.
                        </p>
                        <p style={{ fontSize: 15, color: "#7a5550", lineHeight: 1.9 }}>
                            Today, we are taking that same commitment into the digital age — creating visual, structured books that make complex topics clear and actionable for a new generation of Indian learners.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Pillars ──────────────────────────────────────────────── */}
            <section className="about-section" style={{ padding: "80px 72px", background: "#FDF5F5" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                    <div style={{ textAlign: "center", marginBottom: 56 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 50, padding: "5px 14px", marginBottom: 16 }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#C0392B" }} />
                            <span style={{ color: "#C0392B", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em" }}>OUR EXCELLENCE & VISION</span>
                        </div>
                        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, fontWeight: 800, color: "#1a0a08", letterSpacing: "-0.02em", marginBottom: 10 }}>What Makes Us Different</h2>
                        <p style={{ color: "#7a5550", fontSize: 15, maxWidth: 560, margin: "0 auto" }}>Bharat works hard to make things easy for the readers. We want our passion for excellence to translate into their satisfaction.</p>
                    </div>
                    <div className="about-pillars-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
                        {pillars.map((p, i) => (
                            <div key={i} style={{
                                background: "white", borderRadius: 22,
                                border: "1.5px solid #f0d0c8", padding: 36,
                                boxShadow: "0 4px 18px rgba(192,57,43,0.07)",
                                transition: "all 0.25s", position: "relative", overflow: "hidden"
                            }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = "#C0392B"; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(192,57,43,0.16)"; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = "#f0d0c8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(192,57,43,0.07)"; }}
                            >
                                <div style={{ height: 3, background: "linear-gradient(90deg, #C0392B, #E8523A)", margin: "-36px -36px 28px", borderRadius: "4px 4px 0 0" }} />
                                <div style={{ fontSize: 36, marginBottom: 16 }}>{p.icon}</div>
                                <h4 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800, color: "#1a0a08", marginBottom: 10 }}>{p.title}</h4>
                                <p style={{ fontSize: 14, color: "#7a5550", lineHeight: 1.75 }}>{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Domain grid (replaces marquee) ───────────────────────── */}
            <section className="about-section" style={{ padding: "80px 72px", background: "white", borderTop: "1.5px solid #f0d0c8" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                    <div style={{ textAlign: "center", marginBottom: 48 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 50, padding: "5px 14px", marginBottom: 16 }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#C0392B" }} />
                            <span style={{ color: "#C0392B", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em" }}>AREAS WE COVER</span>
                        </div>
                        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 800, color: "#1a0a08", letterSpacing: "-0.02em" }}>Our Publishing Domains</h2>
                    </div>
                    <div className="about-domains-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
                        {domains.map((d, i) => (
                            <div key={i} style={{
                                background: "#FDF5F5", borderRadius: 18,
                                border: "1.5px solid #f0d0c8",
                                padding: "28px 20px", textAlign: "center",
                                transition: "all 0.22s", cursor: "default"
                            }}
                                onMouseEnter={e => { e.currentTarget.style.background = "#fff0f0"; e.currentTarget.style.borderColor = "#C0392B"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(192,57,43,0.12)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "#FDF5F5"; e.currentTarget.style.borderColor = "#f0d0c8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                            >
                                <div style={{ fontSize: 36, marginBottom: 12 }}>{d.icon}</div>
                                <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontSize: 13, fontWeight: 700, color: "#8B1A1A", lineHeight: 1.4 }}>{d.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


        </div>
    );
}